import axios from "axios";

function getApiBaseUrl() {
  return process.env.REACT_APP_API_URL || "";
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("sundorbonToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("sundorbonToken");
  return token
    ? { accept: "*/*", Authorization: `Bearer ${token}` }
    : { accept: "*/*" };
}

function unwrapList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function pick(...values) {
  for (const v of values) {
    if (v != null && String(v).trim() !== "") return v;
  }
  return "";
}

export function formatDeliveryDays(plan = {}) {
  const label = pick(
    plan.daysLabel,
    plan.DaysLabel,
    plan.deliveryPlanDaysLabel,
    plan.DeliveryPlanDaysLabel,
  );
  if (label) {
    const numeric = String(label).trim();
    if (!numeric) return "";
    return /day/i.test(numeric) ? numeric : `${numeric} day${numeric === "1" ? "" : "s"}`;
  }
  const min = Number(
    plan.minDays ?? plan.MinDays ?? plan.deliveryPlanMinDays ?? plan.days ?? plan.Days ?? 0,
  );
  const max = Number(
    plan.maxDays ?? plan.MaxDays ?? plan.deliveryPlanMaxDays ?? min,
  );
  if (!(min > 0)) return "";
  if (!max || max === min) return `${min} day${min === 1 ? "" : "s"}`;
  return `${min}-${max} days`;
}

export function normalizeDeliveryPlan(row = {}) {
  const minDays = Number(row.minDays ?? row.MinDays ?? row.days ?? row.Days ?? 0);
  const maxDays = Number(row.maxDays ?? row.MaxDays ?? minDays);
  return {
    id: Number(row.id ?? row.Id ?? 0),
    name: pick(row.name, row.Name),
    days: minDays,
    minDays,
    maxDays,
    daysLabel: pick(row.daysLabel, row.DaysLabel),
    routeId: Number(row.routeId ?? row.RouteId ?? 0),
    routeName: pick(row.routeName, row.RouteName),
    sortOrder: Number(row.sortOrder ?? row.SortOrder ?? 0),
    isActive: (row.isActive ?? row.IsActive) === false ? false : true,
  };
}

export function normalizePlanOption(row = {}) {
  const unit = Number(row.unitCost ?? row.UnitCost ?? 0);
  const charge = Number(row.charge ?? row.Charge ?? 0);
  return {
    deliveryPlanId: Number(row.deliveryPlanId ?? row.DeliveryPlanId ?? 0),
    name: pick(row.name, row.Name),
    days: Number(row.minDays ?? row.MinDays ?? row.days ?? row.Days ?? 0),
    minDays: Number(row.minDays ?? row.MinDays ?? row.days ?? row.Days ?? 0),
    maxDays: Number(row.maxDays ?? row.MaxDays ?? row.days ?? row.Days ?? 0),
    daysLabel: pick(row.daysLabel, row.DaysLabel),
    sortOrder: Number(row.sortOrder ?? row.SortOrder ?? 0),
    weightId: Number(row.weightId ?? row.WeightId ?? 0),
    unitCost: Number.isFinite(unit) ? unit : 0,
    charge: Number.isFinite(charge) ? charge : 0,
    minimumWeight: Number(row.minimumWeight ?? row.MinimumWeight ?? 0),
    maximumWeight: Number(row.maximumWeight ?? row.MaximumWeight ?? 0),
    dimensionId: Number(row.dimensionId ?? row.DimensionId ?? 0) || null,
    dimensionName: pick(row.dimensionName, row.DimensionName),
  };
}

export async function getDeliveryPlans() {
  const { data } = await axios.get(`${getApiBaseUrl()}DeliveryPlan`, {
    headers: getAuthHeaders(),
  });
  return unwrapList(data).map(normalizeDeliveryPlan).filter((p) => p.id);
}

export async function getDeliveryPlansByRoute(routeId) {
  if (!routeId) return [];
  const { data } = await axios.get(
    `${getApiBaseUrl()}DeliveryPlan/ByRoute/${routeId}`,
    { headers: getAuthHeaders() },
  );
  return unwrapList(data).map(normalizeDeliveryPlan).filter((p) => p.id);
}

async function fetchWeightPlanOptions(params) {
  const { data } = await axios.get(`${getApiBaseUrl()}Weight/PlanOptions`, {
    params,
    headers: getAuthHeaders(),
  });
  return unwrapList(data)
    .map(normalizePlanOption)
    .filter((p) => p.deliveryPlanId);
}

export async function getWeightPlanOptions({
  routeId,
  kg,
  itemCategoryId,
  dimensionId,
  measurementUnitId,
  volume,
} = {}) {
  if (!routeId || !(Number(kg) > 0)) return [];
  const base = {
    routeId,
    kg,
    itemCategoryId: Number(itemCategoryId) > 0 ? itemCategoryId : undefined,
    dimensionId: Number(dimensionId) > 0 ? dimensionId : undefined,
    measurementUnitId:
      Number(measurementUnitId) > 0 ? measurementUnitId : undefined,
    volume: Number(volume) > 0 ? volume : undefined,
  };

  let rows = await fetchWeightPlanOptions(base);
  if (rows.length) return rows;

  if (base.volume || base.dimensionId) {
    rows = await fetchWeightPlanOptions({
      ...base,
      volume: undefined,
      dimensionId: undefined,
    });
    if (rows.length) return rows;
  }

  if (base.itemCategoryId) {
    rows = await fetchWeightPlanOptions({
      routeId,
      kg,
      measurementUnitId: base.measurementUnitId,
    });
  }
  return rows;
}
